import { useEffect, useRef } from "react";
import { TimeUtils } from "@/utils/TimeUtils";
import { Session } from "./SessionProvider";

export function useSessionManager(
  user: Session["user"],
  recreateSession: () => void
) {
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    const isAuth = !!user;
    if (!isAuth) return;

    const { expiresAt } = user;
    if (!expiresAt) return;

    const refreshBuffer = TimeUtils.minutes(3).toMilliseconds();

    const checkSession = async () => {
      const currentTime = Date.now();
      const timeUntilExpiration = expiresAt - currentTime;

      // Only check in active tab
      if (document.visibilityState !== "visible") {
        if (timeUntilExpiration <= refreshBuffer) {
          // TODO: implement modal to ask user to refresh session
          console.log("Session is about to expire, please refresh the page.");
        }
        return;
      }

      if (timeUntilExpiration <= refreshBuffer) {
        console.log("Refreshing session in active tab...");
        await recreateSession();
      }
    };

    // Set up interval to check periodically
    intervalRef.current = window.setInterval(() => {
      checkSession();
    }, 60_000); // Check every 60 seconds (1 minute)

    document.addEventListener("visibilitychange", checkSession);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", checkSession);
    };
  }, [user, recreateSession]);
}
